// @ts-nocheck — conflicto de tipos entre @types/testing-library__react y @types/react; tsc --noEmit pasa sin errores
import React, { useCallback, useEffect, useState } from 'react'
import {
    Badge,
    Button,
    Dropdown,
    Menu,
    Popconfirm,
    Space,
    Table,
    Tag,
    Tooltip,
    message,
} from 'antd'
import {
    CheckCircleFilled,
    CloseCircleFilled,
    GlobalOutlined,
    LoadingOutlined,
    MinusCircleOutlined,
    SyncOutlined,
} from '@ant-design/icons'
import axios from 'axios'
import { serverUrl } from '../../Constants/env'
import { useSelector } from 'react-redux'

// ─── Types ───────────────────────────────────────────────────────────────────

interface StageRecord {
    ticket: string | null
    status: string
    updatedAt: string | null
}

interface StageStatus {
    status: 'not_started' | 'live' | 'done' | 'error' | 'pending'
    total: number
    done: number
    error: number
    pending: number
    records: StageRecord[]
}

interface FolderRow {
    id: number
    erpCodigo: string
    erpDescripcion: string
    sync: boolean
    facturas: number | null
    items: number | null
    stages: {
        items: StageStatus
        itemUom: StageStatus
        inboundOrders: StageStatus
    }
}

// ─── Stage icon ───────────────────────────────────────────────────────────────

const stageLabels: Record<StageStatus['status'], string> = {
    not_started: 'Sin iniciar',
    live: 'Procesando',
    done: 'Completado',
    error: 'Con errores',
    pending: 'Pendiente',
}

const StageIcon = ({ stage, label }: { stage: StageStatus; label: string }) => {
    const size = 26

    let icon: JSX.Element
    let color: string

    switch (stage.status) {
        case 'done':
            icon = <CheckCircleFilled style={{ fontSize: size, color: '#52c41a' }} />
            color = '#52c41a'
            break
        case 'live':
            icon = <LoadingOutlined style={{ fontSize: size, color: '#1890ff' }} spin />
            color = '#1890ff'
            break
        case 'error':
            icon = <CloseCircleFilled style={{ fontSize: size, color: '#ff4d4f' }} />
            color = '#ff4d4f'
            break
        case 'pending':
            icon = (
                <Badge count={stage.pending} size="small" color="#8c8c8c">
                    <MinusCircleOutlined style={{ fontSize: size, color: '#d9d9d9' }} />
                </Badge>
            )
            color = '#999'
            break
        default:
            icon = <MinusCircleOutlined style={{ fontSize: size, color: '#d9d9d9' }} />
            color = '#d9d9d9'
    }

    const statusLabel = stageLabels[stage.status]
    const records = stage.records || []

    const tooltipTitle = (
        <div>
            <div style={{ fontWeight: 'bold', marginBottom: 2 }}>{label}</div>
            <div style={{ fontSize: 12 }}>
                {statusLabel}
                {stage.total > 0 && ` (${stage.done}/${stage.total})`}
            </div>
            {records.length === 1 && records[0].updatedAt && (
                <div style={{ fontSize: 11, marginTop: 4, color: '#ccc' }}>
                    {records[0].ticket && <span>Ticket {records[0].ticket} · </span>}
                    {records[0].updatedAt}
                </div>
            )}
            {records.length > 1 && (
                <div style={{ fontSize: 11, marginTop: 4 }}>
                    {records.map((r, i) => (
                        <div key={i} style={{ color: '#ccc', marginTop: 2 }}>
                            {r.ticket ? `#${r.ticket}` : '—'} · {r.updatedAt || '...'}
                        </div>
                    ))}
                </div>
            )}
        </div>
    )

    return (
        <Tooltip title={tooltipTitle} placement="top">
            <div style={{ textAlign: 'center', cursor: 'default', minWidth: 60 }}>
                {icon}
                <div style={{ fontSize: 11, color, marginTop: 3, lineHeight: 1 }}>{label}</div>
            </div>
        </Tooltip>
    )
}

// ─── Overall status ───────────────────────────────────────────────────────────

function overallStatus(stages: FolderRow['stages']): 'done' | 'live' | 'in_progress' | 'not_started' {
    const all = [stages.items, stages.itemUom, stages.inboundOrders]
    if (all.every(s => s.status === 'done')) return 'done'
    if (all.some(s => s.status === 'live')) return 'live'
    if (all.some(s => s.status !== 'not_started')) return 'in_progress'
    return 'not_started'
}

// ─── Estado cell — extrae en FC propio para evitar conflicto de tipos ─────────

const EstadoTag: React.FC<{ stages: FolderRow['stages'] }> = ({ stages }) => {
    const status = overallStatus(stages)
    if (status === 'done') return <Tag color="success">Transmitida</Tag>
    if (status === 'live' || status === 'in_progress') return <Tag color="processing">En proceso</Tag>
    return <Tag>Pendiente</Tag>
}

// ─── Actions cell ─────────────────────────────────────────────────────────────

type ActionCellProps = {
    row: FolderRow
    actionLoading: Record<string, boolean>
    onStage: (folder: string, stage: 'items' | 'item-uom' | 'inbound-orders') => void
    onMark: (folder: string) => void
}

const ActionCell: React.FC<ActionCellProps> = ({ row, actionLoading, onStage, onMark }) => {
    const f = row.erpCodigo
    const { stages } = row

    if (overallStatus(stages) === 'done') return null

    const canItems   = stages.items.status === 'not_started' || stages.items.status === 'error'
    const canItemUom = (stages.itemUom.status === 'not_started' || stages.itemUom.status === 'error') && stages.items.status === 'done'
    const canInbound = (stages.inboundOrders.status === 'not_started' || stages.inboundOrders.status === 'error') && stages.items.status === 'done'

    const nextStep: { label: string; stage: 'items' | 'item-uom' | 'inbound-orders' } | null =
        canItems    ? { label: 'Artículos', stage: 'items' } :
        canItemUom  ? { label: 'Códigos',   stage: 'item-uom' } :
        canInbound  ? { label: 'Documentos', stage: 'inbound-orders' } :
        null

    const moreMenu: JSX.Element = (
        <Menu
            onClick={({ key }) => { if (key === 'mark') onMark(f) }}
            items={[{ key: 'mark', label: `Marcar ${f} como transmitida` }]}
        />
    )

    return (
        <Space size={4}>
            {nextStep && (
                <Popconfirm
                    title={`¿Transmitir ${nextStep.label.toLowerCase()} de ${f}?`}
                    okText="Sí"
                    cancelText="No"
                    onConfirm={() => onStage(f, nextStep.stage)}
                >
                    <Button type="primary" size="small" loading={actionLoading[`${f}-${nextStep.stage}`]}>
                        Transmitir: {nextStep.label}
                    </Button>
                </Popconfirm>
            )}
            <Dropdown overlay={moreMenu} trigger={['click']}>
                <Button size="small">···</Button>
            </Dropdown>
        </Space>
    )
}

// ─── Main component ───────────────────────────────────────────────────────────

const ComexIndex = ({ exposeSubMenu, setMainLoading }: any) => {
    const { token } = useSelector((state: any) => state)
    const [folders, setFolders] = useState<FolderRow[]>([])
    const [loading, setLoading] = useState(true)
    const [syncLoading, setSyncLoading] = useState(false)
    const [actionLoading, setActionLoading] = useState<Record<string, boolean>>({})

    const headers = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
    }

    const fetchFolders = useCallback(async () => {
        setLoading(true)
        try {
            const res = await axios.get(`${serverUrl}v3/comex`, { headers })
            setFolders(res.data.folders || [])
        } catch {
            message.error('Error al cargar carpetas COMEX')
        } finally {
            setLoading(false)
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token])

    useEffect(() => {
        exposeSubMenu([])
        setMainLoading(false)
        fetchFolders()
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleSyncERP = async () => {
        setSyncLoading(true)
        try {
            const res = await axios.post(`${serverUrl}v3/comex/sync`, {}, { headers })
            const added: number = res.data.added ?? 0
            message.success(
                added > 0
                    ? `${added} carpeta${added !== 1 ? 's' : ''} nueva${added !== 1 ? 's' : ''} sincronizada${added !== 1 ? 's' : ''}`
                    : 'No hay carpetas nuevas en el ERP'
            )
            if (added > 0) fetchFolders()
        } catch {
            message.error('Error al sincronizar desde ERP')
        } finally {
            setSyncLoading(false)
        }
    }

    const handleMarkTransmitted = async (folder: string) => {
        const key = `${folder}-mark`
        setActionLoading(prev => ({ ...prev, [key]: true }))
        try {
            await axios.post(`${serverUrl}v3/comex/${folder}/mark-transmitted`, {}, { headers })
            message.success(`Carpeta ${folder} marcada como transmitida`)
            fetchFolders()
        } catch {
            message.error('Error al marcar como transmitida')
        } finally {
            setActionLoading(prev => ({ ...prev, [key]: false }))
        }
    }

    const handleStage = async (folder: string, stage: 'items' | 'item-uom' | 'inbound-orders') => {
        const key = `${folder}-${stage}`
        setActionLoading(prev => ({ ...prev, [key]: true }))
        try {
            await axios.post(`${serverUrl}v3/comex/${folder}/${stage}`, {}, { headers })
            message.success('Etapa procesada correctamente')
            fetchFolders()
        } catch (err: any) {
            const msg = err?.response?.data?.message || 'Error al procesar etapa'
            message.error(msg)
        } finally {
            setActionLoading(prev => ({ ...prev, [key]: false }))
        }
    }

    const columns = [
        {
            title: 'Carpeta',
            dataIndex: 'erpCodigo',
            key: 'erpCodigo',
            width: 100,
            render: (code: string) => <strong>{code}</strong>,
        },
        {
            title: 'Proveedor',
            dataIndex: 'erpDescripcion',
            key: 'erpDescripcion',
        },
        {
            title: 'Facturas',
            dataIndex: 'facturas',
            key: 'facturas',
            align: 'right' as const,
            width: 90,
            render: (v: number | null) =>
                v !== null ? v.toLocaleString('es-CL') : <span style={{ color: '#ccc' }}>—</span>,
        },
        {
            title: 'Ítems',
            dataIndex: 'items',
            key: 'items',
            align: 'right' as const,
            width: 90,
            render: (v: number | null) =>
                v !== null ? v.toLocaleString('es-CL') : <span style={{ color: '#ccc' }}>—</span>,
        },
        {
            title: 'Avance Transmisión',
            key: 'stages',
            width: 240,
            render: (_: any, row: FolderRow) => (
                <Space size={8}>
                    <StageIcon stage={row.stages.items} label="Artículos" />
                    <StageIcon stage={row.stages.itemUom} label="Códigos" />
                    <StageIcon stage={row.stages.inboundOrders} label="Documentos" />
                </Space>
            ),
        },
        {
            title: 'Estado',
            key: 'estado',
            width: 130,
            filters: [
                { text: 'Pendiente', value: 'not_started' },
                { text: 'En proceso', value: 'in_progress' },
                { text: 'Transmitida', value: 'done' },
            ],
            defaultFilteredValue: ['not_started', 'in_progress'],
            onFilter: (value: any, record: FolderRow) => {
                const s = overallStatus(record.stages)
                if (value === 'in_progress') return s === 'in_progress' || s === 'live'
                return s === value
            },
            render: (_: any, row: FolderRow) => <EstadoTag stages={row.stages} />,
        },
        {
            title: '',
            key: 'actions',
            width: 220,
            render: (_: any, row: FolderRow) => (
                <ActionCell
                    row={row}
                    actionLoading={actionLoading}
                    onStage={handleStage}
                    onMark={handleMarkTransmitted}
                />
            ),
        },
    ]

    return (
        <div style={{ padding: 24 }}>
            <div
                style={{
                    marginBottom: 16,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                <div>
                    <h2 style={{ margin: 0 }}>Panel COMEX</h2>
                    <div style={{ color: '#999', fontSize: 13, marginTop: 2 }}>
                        Gestión de carpetas de importación y transmisión al WMS
                    </div>
                </div>
                <Space>
                    <Button icon={<SyncOutlined />} onClick={fetchFolders} loading={loading}>
                        Actualizar
                    </Button>
                    <Popconfirm
                        title="¿Sincronizar carpetas nuevas desde el ERP?"
                        okText="Sincronizar"
                        cancelText="Cancelar"
                        onConfirm={handleSyncERP}
                    >
                        <Button type="primary" icon={<GlobalOutlined />} loading={syncLoading}>
                            Sincronizar desde ERP
                        </Button>
                    </Popconfirm>
                </Space>
            </div>

            <Table
                dataSource={folders}
                columns={columns}
                rowKey="erpCodigo"
                loading={loading}
                pagination={false}
                bordered
                size="middle"
                locale={{ emptyText: 'No hay carpetas de importación registradas' }}
                rowClassName={(row) => {
                    const status = overallStatus(row.stages)
                    return status === 'live' ? 'comex-row-active' : ''
                }}
            />
        </div>
    )
}

export default ComexIndex
